import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { useAppContext, Product, ModelType } from "../../context/AppContext";
import { useCopy } from "../../context/CopyContext";
import Header from "../Header";
import Heading from "../Heading";
import Button from "../Button";
import MultiLineCopy from "../MultiLineCopy";
import PoweredBy from "../PoweredBy";
import ItemsModalView from "./ItemsModalView";

const EmptyAcceptedView: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { state, dispatch } = useAppContext();
  const { getCopy } = useCopy();
  const [showItemsModal, setShowItemsModal] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [hasAddedItem, setHasAddedItem] = useState(false);
  
  // Check if this is a back navigation from email capture
  const isBackFromEmail = location.state?.fromEmailCapture === true;

  // Get the product - prioritize from navigation state, then fall back to lastAddedProduct
  const detectedProduct = location.state?.detectedProduct;
  const lastAddedProduct = detectedProduct || state.lastAddedProduct;
  
  // Debug logging
  useEffect(() => {
    console.log('🔍 EmptyAcceptedView - Product info:');
    console.log('- Detected product from navigation:', detectedProduct);
    console.log('- Last added product from state:', state.lastAddedProduct);
    console.log('- Using product:', lastAddedProduct);
  }, [detectedProduct, state.lastAddedProduct, lastAddedProduct]);
  
  // Check if the current product is non-branded
  const isNonBrandedProduct = lastAddedProduct && !lastAddedProduct.isBranded;
  
  // Check if continue should be enabled (branded product OR non-branded product that's been accepted)
  const canContinue = !isNonBrandedProduct || state.hasAcceptedUnidentifiedEmpty;

  // Load products from JSON file based on selected model
  useEffect(() => {
    const loadProducts = async () => {
      try {
        // Load demo products for Demo model, otherwise load full catalog
        const productsFile = state.settings.selectedModel === ModelType.DEMO 
          ? '/demo-products.json' 
          : '/mock-products.json';
        
        const response = await fetch(productsFile);
        const productsData = await response.json();
        setProducts(productsData);
      } catch (error) {
        console.error('Failed to load products:', error);
      }
    };

    loadProducts();
  }, [state.settings.selectedModel]);

  // Don't randomly select a product if we already have one from detection
  useEffect(() => {
    if (products.length > 0 && !hasAddedItem && !isBackFromEmail && !lastAddedProduct) {
      const randomIndex = Math.floor(Math.random() * products.length);
      const selectedProduct = products[randomIndex];
      
      // Preview the selected product without adding to empties yet
      dispatch({ type: 'PREVIEW_PRODUCT', payload: selectedProduct });
      setHasAddedItem(true);
    }
  }, [products, hasAddedItem, dispatch, isBackFromEmail, lastAddedProduct]);

  const handleRemoveItem = () => {
    navigate("/remove-empty");
  };

  const handleContinue = () => {
    // Add the product to cart if we have one
    if (lastAddedProduct) {
      dispatch({ type: "ADD_EMPTY", payload: lastAddedProduct });
    }
    // Set item as dispensed so PlaceEmptyView shows "Thank you!"
    dispatch({ type: "SET_ITEM_DISPENSED", payload: true });
    dispatch({ type: "SET_STEP", payload: 5.5 });
    navigate("/place-empty");
  };

  const handleItemsClick = () => {
    setShowItemsModal(true);
  };

  const handleCloseItemsModal = () => {
    setShowItemsModal(false);
  };

  return (
    <div
      className="bg-brand-background min-h-screen flex flex-col relative overflow-hidden"
      data-node-id="74:974"
    >
      {/* Content Container */}
      <div className="relative z-10 flex-1 flex flex-col gap-24">
        {/* Header Section - with padding */}
        <div className="flex justify-center mx-20 mt-20">
          <div className="w-full max-w-[920px]">
            <Header
              showItems={state.itemsCount > 0}
              itemsNumber={state.itemsCount.toString()}
              onItemsClick={handleItemsClick}
              
            />
          </div>
        </div>

        {/* Main Content Area - with 3-row grid */}
        <motion.div
          className="flex-1 grid grid-rows-3 gap-0 mx-20"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
        >
          {isNonBrandedProduct ? (
            /* Non-branded product layout */
            <>
              {/* Rows 1-2 - Main heading vertically centered */}
              <div className="row-span-2 flex items-center justify-center">
                <div className="text-center w-full max-w-[920px]">
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{
                      duration: 0.6,
                      ease: [0.25, 0.1, 0.25, 1],
                    }}
                  >
                    <Heading title={getCopy('emptyAccepted.identifiedHeading')} />
                  </motion.div>
                </div>
              </div>

              {/* Row 3 - Message vertically centered with max-width 680px */}
              <div className="flex items-end justify-center">
                <motion.div
                  className="text-center w-full max-w-[680px] font-brand-secondary font-light leading-none not-italic text-[32px] text-brand-text-primary"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.6,
                    delay: 0.2,
                    ease: [0.25, 0.1, 0.25, 1],
                  }}
                >
                 <MultiLineCopy 
                  text={getCopy('emptyAccepted.nonBrandedText')}
                  lineClassName="font-normal"
                  className="leading-[42px]"
                />
                </motion.div>
              </div>
            </>
            
          ) : (
            /* Branded product layout */
            <>
              {/* Row 1 - Main heading */}
              <div className="flex items-center justify-center">
                <div className="text-center w-full max-w-[920px]">
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{
                      duration: 0.6,
                      ease: [0.25, 0.1, 0.25, 1],
                    }}
                  >
                    <Heading title={getCopy('emptyAccepted.heading')} />
                  </motion.div>
                </div>
              </div>

              {/* Rows 2 & 3 - Product image and details spanning two rows */}
              <div className="row-span-2 flex flex-col items-center justify-center">
                {/* Product image - taking all available space minus text height */}
                <motion.div
                  className="w-full flex-1 bg-center bg-cover bg-no-repeat"
                  style={{
                    backgroundImage: lastAddedProduct 
                      ? `url('${lastAddedProduct.image}')` 
                      : "url('/brand-ultra-facial-cream.png')",
                    backgroundSize: "contain",
                    backgroundRepeat: "no-repeat",
                  }}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{
                    duration: 0.8,
                    delay: 0.2,
                    ease: [0.25, 0.1, 0.25, 1],
                  }}
                />
                
                {/* Product details - natural flow at bottom */}
                <motion.div
                  className="text-center w-full max-w-[920px] flex-shrink-0"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.6,
                    delay: 0.4,
                    ease: [0.25, 0.1, 0.25, 1],
                  }}
                >
                  <div className="flex flex-col gap-4 items-center py-4">
                    <h3 className="font-brand-secondary font-normal text-[32px] leading-[42px] text-brand-text-primary text-center">
                      {lastAddedProduct ? lastAddedProduct.name : getCopy('productFallback.defaultProductName')}
                    </h3>
                    <div className="flex items-center justify-center">
                      <span className="font-brand-secondary font-normal text-[24px] leading-[31px] text-brand-text-muted text-center">
                        {lastAddedProduct ? lastAddedProduct.size : getCopy('productFallback.defaultProductSize')}
                      </span>
                    </div>
                    
                    {/* Reward Values - Only show for branded products */}
                    {((lastAddedProduct && lastAddedProduct.isBranded && lastAddedProduct.rewards) || (!lastAddedProduct)) && (
                      <motion.div
                        className="flex flex-col gap-2 items-center mt-6"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                          duration: 0.5,
                          delay: 0.6,
                          ease: [0.25, 0.1, 0.25, 1],
                        }}
                      >
                        <div className="font-brand-secondary font-normal text-2xl mb-2 leading-[23px] text-brand-text-primary text-center">
                          Reward value
                        </div>
                        <div className="flex flex-row items-start justify-start">
                          {/* Left section - Points */}
                          <div className="bg-brand-tertiary flex flex-row gap-2.5 items-center justify-center pl-4 pr-3 py-1 rounded-bl-[100px] rounded-tl-[100px]">
                            <div className="font-brand-secondary font-medium text-[24px] leading-[31px] text-white text-center w-[53px]">
                              {lastAddedProduct && lastAddedProduct.rewards ? lastAddedProduct.rewards.loyaltyPoints : 0}
                            </div>
                          </div>
                          {/* Middle section - Dollar Value */}
                          <div className="bg-brand-tertiary flex flex-row gap-2.5 items-center justify-center px-3 py-1 relative border-l-2 border-r-2 border-brand-background">
                            <div className="font-brand-secondary font-medium text-[24px] leading-[31px] text-white text-center whitespace-nowrap">
                              {lastAddedProduct && lastAddedProduct.rewards ? lastAddedProduct.rewards.dollarValue : "$0.00"}
                            </div>
                          </div>
                          {/* Right section - Discount */}
                          <div className="bg-brand-tertiary flex flex-row gap-2.5 items-center justify-center pl-3 pr-4 py-1 rounded-br-[100px] rounded-tr-[100px] w-20">
                            <div className="font-brand-secondary font-medium text-[24px] leading-[31px] text-white text-center flex-1">
                              {lastAddedProduct && lastAddedProduct.rewards ? lastAddedProduct.rewards.discountValue : "0%"}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </div>
                </motion.div>
              </div>
            </>
          )}
        </motion.div>

        {/* Bottom Section - with padding */}
        <div className="flex flex-col gap-16 items-center mx-20 mb-10">
          {/* Buttons */}
          <div className="flex flex-row gap-6 lg:gap-10 w-full">
            <Button variant="secondary" onClick={handleRemoveItem}>
              {getCopy('emptyAccepted.buttons.secondary')}
            </Button>
            <Button 
              variant="primary" 
              onClick={handleContinue}
              disabled={!canContinue}
              className={!canContinue ? 'opacity-50 cursor-not-allowed' : ''}
            >
              {getCopy('emptyAccepted.buttons.primary')}
            </Button>
          </div>



          {/* Footer */}
          <div className="flex justify-center">
            <PoweredBy />
          </div>
        </div>
      </div>

      {/* Items Modal */}
      <ItemsModalView
        isOpen={showItemsModal}
        onClose={handleCloseItemsModal}
      />
    </div>
  );
};

export default EmptyAcceptedView;
